import React from "react";
import { isProductBonusEnabledForCycle } from "@/features/productBonusFeature";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Package,
  Users,
  Lock,
  Link,
  Info,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { QuantityStepper } from "./QuantityStepper";
import { ProductMigrationNotice, type ProductMigrationInfo } from "./ProductMigrationNotice";
import { isProgrammedOrderItem } from "@/lib/utils";
import { calculateSeriesTotals, getPreviousYearPrice as getSeriesPreviousYearPrice } from "@/lib/seriesTotals";
import { getSecondSemesterAdjustmentDeadline } from "@/lib/orderCycle";
import { formatDate } from "@/lib/formatters";
interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  adjustment: number;
  quantidade_pedido: number;
  ordered_quantity?: number; // Quantidade já pedida anteriormente
  valor_liquido: number;
  valor_total: number;
  brand_id: number;
  previousYearProductPrice?: number | null;
  product_migration?: ProductMigrationInfo | null;
  bonified_quantity?: number;
  available_bonus_quantity?: number;
  scheduling_category?: string | null;
  is_optional?: boolean;
}
interface SeriesCardProps {
  serie: string;
  items: OrderItem[];
  isOpen: boolean;
  onToggle: () => void;
  onUpdateQuantity: (
    itemId: number,
    quantity: number,
    updateAllInSeries?: boolean
  ) => void;
  formatCurrency: (value: number) => string;
  cycleYear?: number;
}
export const SeriesCard = ({
  serie,
  items,
  isOpen,
  onToggle,
  onUpdateQuantity,
  formatCurrency,
  cycleYear = 2026,
}: SeriesCardProps) => {
  const isProductBonusEnabled = isProductBonusEnabledForCycle(cycleYear);
  const hasAboveContracted = items.some(
    (item) => item.quantidade_pedido > item.quantidade_contratual
  );

  // Check if this is professor material
  const isProfessorMaterial = items.some(
    (item) => item.utilizacao?.toLowerCase() === "professor"
  );

  // Totais do cabeçalho: só o que é cobrado agora (regra e motivo em lib/seriesTotals).
  const { seriesTotal, currentPrice: totalPreco2026, previousYearPrice: totalPreviousYearPrice } =
    calculateSeriesTotals(items, cycleYear, true);

  const availableBonusTotal = items.reduce((total, item) => {
    return total + (item.available_bonus_quantity ?? 0);
  }, 0);

  const orderQuantityTotal = items.reduce((total, item) => {
    return total + item.quantidade_pedido;
  }, 0);

  const availableBonusBalanceTotal = items.reduce((total, item) => {
    return total + Math.max(
      (item.available_bonus_quantity ?? 0) - item.quantidade_pedido,
      0,
    );
  }, 0);

  const firstItem = items[0];

  // Ano do ciclo atual e rótulo dinâmico do ano anterior
  const previousYear = cycleYear - 1;
  const previousYearLabel = `Preço por Aluno ${previousYear}`;

  // Preço do produto no ciclo anterior (null = campo oculto); regra em lib/seriesTotals.
  const getPreviousYearPrice = (item: OrderItem): number | null => getSeriesPreviousYearPrice(item, cycleYear);


  // Get the maximum quantidade_contratual from all items in the series
  const maxQuantidadeContratual = Math.max(
    ...items.map((item) => item.quantidade_contratual)
  );

  // Check if series has products from restricted brands (AZ: 1, High Five: 2)
  const hasRestrictedBrandProducts = () => {
    return items.some((item) => item.brand_id === 1 || item.brand_id === 2);
  };

  // Check if quantity can be updated (blocked for restricted brands when expanded)
  const canUpdateQuantity = (item?: OrderItem) => {
    if (!isOpen) return true; // Always allow when collapsed
    if (item) {
      return !(item.brand_id === 1 || item.brand_id === 2); // Block only AZ and High Five when expanded
    }
    return true;
  };

  const updateAllItems = (quantity: number) => {
    // Update all items in the series with the same quantidade_pedido
    // The quantity is based on the highest quantidade_contratual in the series
    // Pass updateAllInSeries=true to ensure all items in the series are updated
    if (items.length > 0) {
      onUpdateQuantity(items[0].id, quantity, true);
    }
  };

  // Determine card styling based on quantity comparison
  const getCardStyling = () => {
    if (hasAboveContracted) {
      return "border-blue-200 bg-blue-50/30";
    }
    return "hover:shadow-md";
  };
  return (
    <Card className={`mb-4 ${getCardStyling()}`}>
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <div className="p-6 cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  {isOpen ? (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-3 ">
                    <Badge
                      variant="outline"
                      className="text-sm font-medium px-3 py-1"
                    >
                      {serie}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {firstItem && (
                  <div className="flex items-center gap-2">
                    {isProfessorMaterial && isProductBonusEnabled && (
                      <div className="px-2 py-1.5 bg-green-50 rounded text-center min-w-[105px]">
                        <div className="text-[10px] text-green-700 mb-0.5 font-medium whitespace-nowrap">
                          Qtde bonificada total
                        </div>
                        <div className="font-semibold text-xs text-green-700">
                          {availableBonusTotal}
                        </div>
                      </div>
                    )}

                    {isProfessorMaterial && isProductBonusEnabled && (
                      <div className="px-2 py-1.5 bg-gray-100 rounded text-center min-w-[85px]">
                        <div className="text-[10px] text-gray-600 mb-0.5 font-medium whitespace-nowrap">
                          Quantidade do pedido
                        </div>
                        <div className="font-semibold text-xs text-gray-900">
                          {orderQuantityTotal}
                        </div>
                      </div>
                    )}

                    {isProfessorMaterial && isProductBonusEnabled && (
                      <div className="px-2 py-1.5 bg-green-50 rounded text-center min-w-[125px]">
                        <div className="text-[10px] text-green-700 mb-0.5 font-medium whitespace-nowrap">
                          Saldo bonificado disponível
                        </div>
                        <div className="font-semibold text-xs text-green-700">
                          {availableBonusBalanceTotal}
                        </div>
                      </div>
                    )}

                    {!isProfessorMaterial && (
                      <>
                        {/* Preço do ciclo anterior — oculto quando null (produto novo em ciclo 2027+) */}
                        {totalPreviousYearPrice !== null && (
                          <div className="px-2 py-1.5 bg-gray-100 rounded text-center min-w-[85px]">
                            <div className="text-[10px] text-gray-600 mb-0.5 font-medium">
                              {previousYearLabel}
                            </div>
                            <div className="font-semibold text-xs text-gray-900">
                              {formatCurrency(totalPreviousYearPrice)}
                            </div>
                          </div>
                        )}

                        {/* Hidden but data still available: Qtd. Referência Aluno = {firstItem.quantidade_contratual} */}

                        <div className="px-2 py-1.5 bg-gray-100 rounded text-center min-w-[85px]">
                          <div className="text-[10px] text-gray-600 mb-0.5 font-medium">
                            Preço por Aluno {cycleYear}
                          </div>
                          <div className="font-semibold text-xs text-gray-900">
                            {formatCurrency(totalPreco2026)}
                          </div>
                        </div>
                      </>
                    )}

                    {!isProfessorMaterial && (
                      <div className="px-2 py-1.5 bg-gray-100 rounded min-w-[105px]">
                        <div className="text-[10px] text-gray-600 mb-0.5 font-medium leading-tight whitespace-nowrap flex items-center justify-center gap-1">
                          <span>Qtd. Pedido</span>
                        </div>
                        <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                          <QuantityStepper
                            size="sm"
                            value={firstItem.quantidade_pedido || 0}
                            productName={serie}
                            onDecrement={() => updateAllItems(Math.max((firstItem.quantidade_pedido || 0) - 1, 0))}
                            onIncrement={() => updateAllItems((firstItem.quantidade_pedido || 0) + 1)}
                            onChange={value => updateAllItems(value)}
                            valueClassName={
                              firstItem.quantidade_pedido < maxQuantidadeContratual
                                ? "text-yellow-600"
                                : firstItem.quantidade_pedido > maxQuantidadeContratual
                                  ? "text-blue-600"
                                  : "text-gray-900"
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="px-3 py-1.5 bg-green-50 rounded text-center min-w-[90px] ml-2">
                  <div className="text-sm font-bold text-green-600">
                    {formatCurrency(seriesTotal)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
          <div className="px-6 pb-6 space-y-3">
            <div className="border-t pt-4">
              {items.map((item) => {
                const isAboveContracted =
                  item.quantidade_pedido > item.quantidade_contratual;
                const price2025 = getPreviousYearPrice(item);
                const isScheduledItem = isProgrammedOrderItem(item, cycleYear);
                const getItemStyling = () => {
                  if (isAboveContracted) {
                    return "border-blue-200 bg-blue-50/30";
                  }
                  return "border-border";
                };
                const isQuantityChanged =
                  item.quantidade_pedido !== item.quantidade_contratual;
                return (
                  <div
                    key={item.id}
                    className={`p-4 rounded-lg border bg-card ${getItemStyling()} mb-3`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 pr-6">
                        <div className="font-medium text-foreground mb-2">
                          {item.descricao_produto}
                        </div>
                        <ProductMigrationNotice info={item.product_migration} cycleYear={cycleYear} formatCurrency={formatCurrency} />
                        {cycleYear >= 2027 && item.scheduling_category === "ImmediateAndExam" && (
                          <div className="bg-blue-50 border border-dashed border-blue-300 text-blue-700 text-sm mt-2 p-2 rounded flex items-center gap-2">
                            <Info className="h-5 w-5" />
                            Item faturado neste pedido. Também gera o pedido programado de avaliação/simulado com a mesma quantidade.
                          </div>
                        )}
                        {isScheduledItem && cycleYear >= 2027 && item.scheduling_category !== "ExamScheduled" && item.scheduling_category !== "ImmediateAndExam" && (
                          <div className="bg-blue-50 border border-dashed border-blue-300 text-blue-700 text-sm mt-2 p-2 rounded flex items-center gap-2">
                            <Info className="h-5 w-5" />
                            Pedido programado 2º semestre. As quantidades poderão ser ajustadas até {formatDate(getSecondSemesterAdjustmentDeadline(cycleYear))}.
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-6">
                        {isProfessorMaterial ? (
                          <>
                            {(() => {
                              const prevPrice = getPreviousYearPrice(item);
                              return prevPrice !== null ? (
                                <div className="text-right space-y-1 w-32">
                                  <div className="text-xs text-muted-foreground">
                                    Preço por Professor {previousYear}
                                  </div>
                                  <div className="font-medium text-sm">
                                    {formatCurrency(prevPrice)}
                                  </div>
                                </div>
                              ) : null;
                            })()}

                            <div className="text-right space-y-1 w-32">
                              <div className="text-xs text-muted-foreground">
                                Preço por Professor {cycleYear}
                              </div>
                              <div className="font-medium text-primary text-sm">
                                {formatCurrency(item.valor_liquido ? item.valor_liquido : item.preco)}
                              </div>
                            </div>

                            {!isProductBonusEnabled && (
                              <div className="text-center w-20">
                                <div className="text-xs text-muted-foreground mb-1">
                                  Qtd. Referência Professor
                                </div>
                                <Badge variant="outline" className="text-sm">
                                  {item.quantidade_contratual}
                                </Badge>
                              </div>
                            )}

                            {!isProductBonusEnabled && (
                              <div className="text-center w-20">
                                <div className="text-xs text-muted-foreground mb-1">
                                  Desconto
                                </div>
                                <Badge
                                  variant="secondary"
                                  className="bg-green-100 text-sm text-green-700"
                                >
                                  {item.desconto.toFixed(2)}%
                                </Badge>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {(() => {
                              const prevPrice = getPreviousYearPrice(item);
                              return prevPrice !== null ? (
                                <div className="text-right space-y-1 w-32">
                                  <div className="text-xs text-muted-foreground">
                                    {previousYearLabel}
                                  </div>
                                  <div className="font-medium text-sm">
                                    {formatCurrency(prevPrice)}
                                  </div>
                                </div>
                              ) : null;
                            })()}

                            {/* Hidden but data still available: Qtd. Referência Aluno = {item.quantidade_contratual} */}

                            <div className="text-center w-16">
                              <div className="text-xs text-muted-foreground mb-1">
                                Reajuste
                              </div>
                              <Badge
                                variant="secondary"
                                className="bg-orange-400 text-white text-xs px-2 py-1"
                              >
                                {item.adjustment.toFixed(2)}%
                              </Badge>
                            </div>

                            <div className="text-right space-y-1 w-32">
                              <div className="text-xs text-muted-foreground">
                                Preço por Aluno {cycleYear}
                              </div>
                              <div className="font-medium text-primary text-sm">
                                {formatCurrency(item.valor_liquido)}
                              </div>
                            </div>
                          </>
                        )}

                        {isProfessorMaterial && isProductBonusEnabled && (
                          <div className="text-center w-24">
                            <div className="text-xs text-muted-foreground mb-1">
                              Qtde bonificada total
                            </div>
                            <Badge
                              variant="outline"
                              className="border-green-200 bg-green-50 text-sm font-semibold text-green-700"
                            >
                              {item.available_bonus_quantity ?? 0}
                            </Badge>
                          </div>
                        )}

                        <div className="text-center">
                          <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                            <span>
                              {isProfessorMaterial && isProductBonusEnabled
                                ? "Quantidade do pedido"
                                : "Qtd. Pedido"}
                            </span>
                          </div>
                          {isProfessorMaterial ? (
                            <div className="flex justify-center">
                              <QuantityStepper
                                size="sm"
                                value={item.quantidade_pedido}
                                productName={item.descricao_produto}
                                onDecrement={() => onUpdateQuantity(item.id, Math.max(item.quantidade_pedido - 1, 0))}
                                onIncrement={() => onUpdateQuantity(item.id, item.quantidade_pedido + 1)}
                                onChange={value => onUpdateQuantity(item.id, value)}
                              />
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-sm">
                              {item.quantidade_pedido}
                            </Badge>
                          )}
                        </div>

                        {isProfessorMaterial && isProductBonusEnabled && (
                          <div className="text-center w-24">
                            <div className="text-xs text-muted-foreground mb-1">
                              Saldo bonificado disponível
                            </div>
                            <Badge
                              variant="outline"
                              className="border-green-200 bg-green-50 text-sm font-semibold text-green-700"
                            >
                              {Math.max(
                                (item.available_bonus_quantity ?? 0) - item.quantidade_pedido,
                                0,
                              )}
                            </Badge>
                          </div>
                        )}

                        <div className="text-right w-32">
                          <div className="text-xs text-muted-foreground mb-1">
                            Total
                          </div>
                          <div className="text-xl font-bold text-green-600">
                            {formatCurrency(item.valor_total)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
